/**
 * El estado de entrega de cada burbuja que salió de nuestro lado: uno, siempre.
 *
 * POR QUÉ EXISTE (03-nov-2026)
 * ----------------------------
 * Ruben, en PROD: «en algunos sale enviado y en otros no […] necesitamos
 * corregir esa ambigüedad». La burbuja pintaba el estado solo si lo había, y
 * en PROD 67 de 122 mensajes del bot y los 7 del asesor no lo tenían: el
 * sistema no anotaba el envío de lo que se guardaba antes de mandarse. Una
 * burbuja sin nada se leía como «no salió», y sí había salido.
 *
 * Desde ese día todo lo que sale queda con estado (conversation/handler,
 * `_anotar_envio`). Lo anterior no se puede reconstruir: dice «sin registro»,
 * en gris, en vez de callarse o inventar.
 *
 * QUÉ QUIERE DECIR «ENVIADO»
 * --------------------------
 * Depende de por dónde salió (`canal_envio`). Por nuestro número, WhatsApp lo
 * aceptó y faltan sus avisos de entregado y leído. Por Software, Software lo
 * recibió para publicarlo, y Software no confirma la entrega: 0 acuses en TEST
 * y en PROD, medido ese mismo día. Hasta que lo haga, «enviado» es lo más alto
 * que puede decir una burbuja de una conversación por Software.
 *
 * Puro y sin imports: lo corre `tests/unit/test_estado_de_entrega_portal.py`
 * en Node.
 */

export type TonoDeEntrega = 'normal' | 'fallo' | 'tenue'

export interface EstadoDeEntrega {
  /** Lo que se lee al pie de la burbuja: «enviado», «leído»… */
  etiqueta: string
  /** El `title`: qué significa ese estado acá. */
  ayuda: string
  tono: TonoDeEntrega
}

export interface MensajeConEntrega {
  sender: string
  message_id?: string
  delivery_status?: string
  canal_envio?: string
}

/** Lo que escribió el cliente —a nosotros o al precalificador— y lo que le
 *  contestó el precalificador del socio no salió de nuestro lado. */
const AJENOS = ['user', 'precal_cliente', 'precal_bot']

export const AYUDA_ENVIADO_SOCIO =
  'Software lo recibió para enviarlo. Software todavía no confirma si le llegó al cliente.'
export const AYUDA_ENVIADO_PROPIO =
  'WhatsApp lo aceptó. Todavía no confirma que le llegó al cliente.'
export const AYUDA_ENVIADO =
  'Salió de nuestro lado. Todavía no hay confirmación de que le llegó al cliente.'
export const AYUDA_SIN_REGISTRO =
  'Sin registro del envío: es anterior al 03-nov-2026 y no se sabe si le llegó al cliente.'

export function estadoDeEntrega(m: MensajeConEntrega): EstadoDeEntrega | null {
  if (AJENOS.includes(m.sender)) return null
  // La burbuja optimista del asesor, hasta que el sondeo trae la de verdad.
  if ((m.message_id || '').startsWith('optimista-')) {
    return { etiqueta: 'enviando…', ayuda: 'Se está enviando.', tono: 'tenue' }
  }
  const estado = (m.delivery_status || '').trim()
  switch (estado) {
    case 'failed':
      return { etiqueta: 'no entregado', ayuda: 'No le llegó al cliente.', tono: 'fallo' }
    case 'read':
      return { etiqueta: 'leído', ayuda: 'El cliente lo leyó.', tono: 'normal' }
    case 'delivered':
      return { etiqueta: 'entregado', ayuda: 'Le llegó al cliente.', tono: 'normal' }
    case 'sent':
      return {
        etiqueta: 'enviado',
        ayuda: m.canal_envio === 'socio' ? AYUDA_ENVIADO_SOCIO
          : m.canal_envio === 'propio' ? AYUDA_ENVIADO_PROPIO
            : AYUDA_ENVIADO,
        tono: 'normal',
      }
    case '':
      return { etiqueta: 'sin registro', ayuda: AYUDA_SIN_REGISTRO, tono: 'tenue' }
    default:
      // Un estado que el backend agregue y esto no conozca: se muestra tal
      // cual antes que callarlo.
      return { etiqueta: estado, ayuda: '', tono: 'normal' }
  }
}
